import React, { useCallback } from 'react';
import { Radio, Input, InputNumber, DatePicker, Select, TreeSelect } from 'antd';
import type { RangePickerProps } from 'antd/es/date-picker';
import { RadioChangeEvent } from 'antd/es/radio/interface';
import moment, { Moment } from 'moment';

import { IMeta, ITreeSelectRenderNode, IValueState, IFieldDetailItem } from '../type';

import InputNumberRange from './InputNumberRange';
import style from './index.less';
import TagSelect from '../../tag-select';

const { SHOW_PARENT, SHOW_CHILD, TreeNode } = TreeSelect;

const preDay: moment.Moment = moment().subtract(1, 'day');
const todayStr = `${moment().format('YYYY-MM-DD')} 23:59:59`;
const seven = `${moment()
  .subtract(7, 'day')
  .format('YYYY-MM-DD')} 00:00:00`;
const month = `${moment()
  .subtract(30, 'day')
  .format('YYYY-MM-DD')} 00:00:00`;
const dateRange: RangePickerProps['ranges'] = {
  ['今天']: [moment(`${moment().format('YYYY-MM-DD')} 00:00:00`), moment(todayStr)],
  ['昨天']: [
    moment(`${preDay.format('YYYY-MM-DD')} 00:00:00`),
    moment(`${preDay.format('YYYY-MM-DD')} 23:59:59`)
  ],
  ['过去7天']: [moment(seven), moment(todayStr)],
  ['过去30天']: [moment(month), moment(todayStr)]
};

const defaultPickerValue: RangePickerProps['defaultPickerValue'] = [
  moment().subtract(1, 'month'),
  moment().subtract(1, 'month')
];

type Prop = {
  field: string;
  /** 字段的类型'string', 'number', 'date', 'radio', 'check' */
  type: string;
  /** 搜索条件具体项选择选中回调 */
  onRadioChange?(value: string | number): void;
  /** 搜索条件具体选中项 */
  selectedRadio: string;
  /** 搜索条件项改变回调 */
  onValueStateChange?(valueState: IValueState): void;
  /**
   * check radio 选项下拉数据列表
   * 结构 ['选项1','选项2']
   * */
  metaInfo: ITreeSelectRenderNode[];
  /**
   * 扩展的meta，主要是针对于复杂key vaule并且metaInfo不能满足情况的时候使用，如果有数据就默认为下面的输入框是<Select />
   * 结构 [{label:'名称',value:'name'}]
   * */
  extMeta?: IMeta[];
  /** radio的列表，因为名称不一定对应字段名称，所以需要重新传入一份配置
   * 结构[{label:'名称',value:'name'}]
   * */
  radioList: IFieldDetailItem[];
  /** 最大的时间 */
  maxDate?: string;
  /** 最小的时间 */
  minDate?: string;
  treeDefaultExpandedKeys: string[];
  /** 单位 */
  unit?: string;
  /** 自定义选项的node节点，返回render的东西 */
  renderNode?(label: string | number, value: string | number, field: string): React.ReactNode;
  onDropdownVisibleChange?(visible: boolean): void;
  valueState: IValueState;
};

const FieldDetail: React.FC<Prop> = ({
  field,
  type = '',
  onRadioChange,
  selectedRadio = '',
  valueState,
  onValueStateChange,
  metaInfo = [],
  radioList,
  extMeta = [],
  maxDate = '2099-12-30 00:00:00',
  minDate = '1798-12-30 00:00:00',
  treeDefaultExpandedKeys = [],
  unit = '',
  renderNode,
  onDropdownVisibleChange
}) => {

  const radioChange = useCallback((e: RadioChangeEvent) => {
    if (onRadioChange) {
      onRadioChange(e.target.value);
    }
  }, []);

  const onSelectFilter = useCallback(() => {
    return false;
  }, []);

  const disabledDate = useCallback(
    (date: Moment) => {
      if (!date) {
        return true;
      }
      try {
        const min = moment(minDate);
        const max = moment(maxDate);
        return min.valueOf() > date.valueOf() || date.valueOf() >= max.valueOf();
      } catch (e) {
        return true;
      }
    },
    [minDate, maxDate]
  );

  const renderCheckNode = useCallback(
    (list: ITreeSelectRenderNode[] = []) => {
      return list.map(item => {
        const { value, label, children } = item;
        let titleEle = null;
        if (renderNode) {
          titleEle = renderNode(label, value, field);
        }
        if (children && children.length) {
          return (
            <TreeNode key={value} value={value} title={titleEle || String(label ?? '')}>
              {renderCheckNode(children)}
            </TreeNode>
          );
        }
        return <TreeNode key={value} value={value} title={titleEle || String(label ?? '')} />;
      });
    },
    [renderNode]
  );

  const renderInput = (value: string | number, valueState: IValueState, isSelected: boolean) => {
    const valueStateCopy = { ...valueState };
    const rowValue = valueState[value];
    const className = `${!isSelected ? style.hidden : ''} ${style.text_input}`;
    if (extMeta && extMeta.length) {
      return (
        <Select
          value={typeof rowValue === 'string' ? rowValue : ''}
          className={className}
          optionFilterProp='title'
          filterOption={onSelectFilter}
          onDropdownVisibleChange={(e: boolean) => {
            if (onDropdownVisibleChange) {
              onDropdownVisibleChange(e);
            }
          }}
          onChange={(e: string) => {
            valueStateCopy[value] = e;
            if (onValueStateChange) {
              onValueStateChange(valueStateCopy);
            }
          }}
        >
          {extMeta.map((item, i) => {
            return (
              <Select.Option key={`${item.value}_${i}`} label={item.label} value={item.value}>
                {item.label}
              </Select.Option>
            );
          })}
        </Select>
      );
    }

    if (field === 'sources') {
      const treeCheckValue: string[] = Array.isArray(rowValue) ? (rowValue as string[]) : [];
      return (
        <TreeSelect
          treeDefaultExpandedKeys={treeDefaultExpandedKeys || []}
          showSearch
          treeNodeFilterProp='title'
          className={className}
          value={treeCheckValue}
          filterOption={true}
          onDropdownVisibleChange={onDropdownVisibleChange}
          multiple
          dropdownClassName={style.tree_dropdown}
          treeCheckable={true}
          showCheckedStrategy={SHOW_CHILD}
          onChange={e => {
            valueStateCopy[value] = e;
            if (onValueStateChange) {
              onValueStateChange(valueStateCopy);
            }
          }}
        >
          {renderCheckNode(metaInfo)}
        </TreeSelect>
      );
    }
    if (field === 'tag') {
      return (
        <div className={className}>
          <TagSelect
            multiple
            value={rowValue as number[]}
            onChange={e => {
              valueStateCopy[value] = e;
              if (onValueStateChange) {
                onValueStateChange(valueStateCopy);
              }
            }}
            data={metaInfo}
          />
        </div>
      );
    }

    if (type === 'treeCheck') {
      const treeCheckValue: string[] = Array.isArray(rowValue) ? (rowValue as string[]) : [];
      return (
        <TreeSelect
          treeDefaultExpandedKeys={treeDefaultExpandedKeys || []}
          showSearch
          treeNodeFilterProp='title'
          className={className}
          value={treeCheckValue}
          filterOption={true}
          onDropdownVisibleChange={e => {
            if (onDropdownVisibleChange) {
              onDropdownVisibleChange(e);
            }
          }}
          multiple
          dropdownClassName={style.tree_dropdown}
          treeCheckable={true}
          showCheckedStrategy={SHOW_PARENT}
          onChange={e => {
            valueStateCopy[value] = e;
            if (onValueStateChange) {
              onValueStateChange(valueStateCopy);
            }
          }}
        >
          {renderCheckNode(metaInfo)}
        </TreeSelect>
      );
    }

    if (type === 'tree') {
      const treeValue: string | number = ['string', 'number'].includes(typeof rowValue)
        ? (rowValue as string | number)
        : '';
      return (
        <TreeSelect
          treeDefaultExpandedKeys={treeDefaultExpandedKeys}
          showSearch
          treeNodeFilterProp='title'
          className={className}
          onDropdownVisibleChange={e => {
            if (onDropdownVisibleChange) {
              onDropdownVisibleChange(e);
            }
          }}
          value={treeValue}
          filterOption={true}
          dropdownClassName={style.tree_dropdown}
          onChange={(e: string | number) => {
            valueStateCopy[value] = e;
            if (onValueStateChange) {
              onValueStateChange(valueStateCopy);
            }
          }}
        >
          {renderCheckNode(metaInfo)}
        </TreeSelect>
      );
    }

    if (type === 'string') {
      const inputValue: string | number = ['string', 'number'].includes(typeof rowValue)
        ? (rowValue as string | number)
        : '';
      return (
        <Input
          className={className}
          placeholder={'请输入'}
          maxLength={120}
          value={inputValue}
          onChange={e => {
            const { target } = e;
            valueStateCopy[value] = target.value;
            if (onValueStateChange) {
              onValueStateChange(valueStateCopy);
            }
          }}
        />
      );
    }

    if (type === 'number') {
      let max = 999999999999;
      // 写死年龄最大127
      if (field === 'age') {
        max = 127;
      }
      if (value === 'range') {
        const inputNumberRangeValue: string = typeof rowValue === 'string' ? rowValue : '';
        return (
          <InputNumberRange
            className={className}
            value={inputNumberRangeValue}
            unit={unit}
            onChange={e => {
              valueStateCopy[value] = e;
              if (onValueStateChange) {
                onValueStateChange(valueStateCopy);
              }
            }}
          />
        );
      }
      const numberValue: number = typeof rowValue === 'number' ? rowValue : 0;
      return (
        <div className={`${className} ${style.number_flex}`}>
          <div className={style.number_all}>
            <InputNumber
              className={style.text_number}
              maxLength={50}
              min={0}
              max={max}
              value={numberValue}
              onChange={e => {
                valueStateCopy[value] = e;
                if (onValueStateChange) {
                  onValueStateChange(valueStateCopy);
                }
              }}
            />
          </div>
          {unit ? <div className={style.unit}>{unit}</div> : null}
        </div>
      );
    }

    if (type === 'date') {
      if (value === 'range') {
        let dateValue: RangePickerProps['value'] = null;
        if (Array.isArray(rowValue) && moment.isMoment(rowValue[0]) && moment.isMoment(rowValue[1])) {
          dateValue = [rowValue[0], rowValue[1]];
        }
        return (
          <DatePicker.RangePicker
            disabledDate={disabledDate}
            defaultPickerValue={defaultPickerValue}
            showTime={{
              hideDisabledOptions: true,
              defaultValue: [moment('00:00:00', 'HH:mm:ss'), moment('23:59:59', 'HH:mm:ss')]
            }}
            className={`${className} ${style.picker_range}`}
            value={dateValue}
            ranges={dateRange}
            format='YYYY/MM/DD HH:mm:ss'
            onChange={e => {
              valueStateCopy[value] = e;
              if (onValueStateChange) {
                onValueStateChange(valueStateCopy);
              }
            }}
          />
        );
      }
      const dateValue: Moment | null = rowValue && moment.isMoment(rowValue) ? rowValue : null;
      return (
        <DatePicker
          disabledDate={disabledDate}
          showTime
          className={`${className} ${style.time_picker}`}
          value={dateValue}
          onChange={e => {
            valueStateCopy[value] = e;
            if (onValueStateChange) {
              onValueStateChange(valueStateCopy);
            }
          }}
        />
      );
    }

    if (type === 'radio') {
      const radioValue: string | number = ['string', 'number'].includes(typeof rowValue)
        ? (rowValue as string | number)
        : '';
      return (
        <Select
          value={radioValue}
          optionFilterProp='label'
          className={className}
          onDropdownVisibleChange={e => {
            if (onDropdownVisibleChange) {
              onDropdownVisibleChange(e);
            }
          }}
          onChange={(e: string | number) => {
            valueStateCopy[value] = e;
            if (onValueStateChange) {
              onValueStateChange(valueStateCopy);
            }
          }}
        >
          {metaInfo.map(item => {
            return (
              <Select.Option key={item.value} label={item.label} value={item.value}>
                {item.label}
              </Select.Option>
            );
          })}
        </Select>
      );
    }
    if (type === 'check') {
      const checkValue: string[] | number[] = Array.isArray(rowValue) ? (rowValue as string[] | number[]) : [];
      return (
        <TreeSelect
          onDropdownVisibleChange={e => {
            if (onDropdownVisibleChange) {
              onDropdownVisibleChange(e);
            }
          }}
          showSearch
          treeNodeFilterProp='title'
          className={className}
          value={checkValue}
          filterOption={true}
          multiple
          dropdownClassName={`${style.tree_dropdown} ${style.check_tree}`}
          treeCheckable={true}
          showCheckedStrategy={SHOW_PARENT}
          onChange={e => {
            valueStateCopy[value] = e;
            if (onValueStateChange) {
              onValueStateChange(valueStateCopy);
            }
          }}
        >
          {renderCheckNode(metaInfo)}
        </TreeSelect>
      );
    }

    return null;
  };

  return (
    <div>
      <Radio.Group className={style.radio_group} value={selectedRadio} onChange={radioChange}>
        {radioList.map((item, i) => {
          const { value, disabled } = item;
          let { label } = item;
          const isSelected = selectedRadio === value;
          if (type === 'date' && value === 'eq') {
            label = '等于';
          }
          return (
            <div className={style.row} key={`${value}_${i}`}>
              <Radio value={value}>{String(label ?? '')}</Radio>
              <div className={`${disabled ? style.hidden : ''}`}>{renderInput(value, valueState, isSelected)}</div>
            </div>
          );
        })}
      </Radio.Group>
    </div>
  );
};

export default FieldDetail;
